import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentBox from '../ContentBox';
import { getFranklyNetLinks, getAskFrankTrending, getAskFrankStatus } from '../../services/api';
import { frameUrl } from './frameLinks';
import { deskPath, originsHomeUrl } from './franklyNetHost';

function LinkCard({ link, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(link.url)}
            className="group flex w-full items-stretch gap-3 overflow-hidden rounded-[10px] border border-gray-300 bg-white text-left transition-transform hover:-translate-y-[1px]"
            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
        >
            <div className="h-[64px] w-[96px] shrink-0 overflow-hidden bg-gray-200">
                {link.thumbnailUrl ? (
                    <img
                        src={link.thumbnailUrl}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] uppercase text-gray-400">
                        AskFrank
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1 py-2 pr-3">
                <p className="truncate font-['Jersey_10'] text-[16px] uppercase leading-tight tracking-[1px] text-maroon">
                    {link.title}
                </p>
                {link.subtitle && (
                    <p className="mt-[2px] line-clamp-2 text-[12px] leading-snug text-gray-600">{link.subtitle}</p>
                )}
            </div>
        </button>
    );
}

function FranklyNetHome({ onSearch }) {
    const [links, setLinks] = useState([]);
    const [trending, setTrending] = useState([]);
    const [status, setStatus] = useState({ indexedPages: 0, approvedSources: 0 });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;

        Promise.all([getFranklyNetLinks(), getAskFrankTrending(), getAskFrankStatus()])
            .then(([linkRes, trendRes, statusRes]) => {
                if (!active) return;
                if (linkRes.success) setLinks(linkRes.links || []);
                if (trendRes.success) setTrending(trendRes.trending || []);
                if (statusRes.success) setStatus(statusRes);
            })
            .catch(() => {})
            .finally(() => { if (active) setLoading(false); });

        return () => { active = false; };
    }, []);

    const openLink = (url) => navigate(frameUrl(url));

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
                <ContentBox theme="default" tint="red" header="Welcome to AskFrank">
                    <p className="text-[14px] leading-relaxed text-gray-700">
                        AskFrank is Frank&apos;s corner of the web. Ask him a question up top and he will
                        read what the Habbo fansites have to say, then point you at the pages worth your time.
                    </p>

                    {status.indexedPages > 0 && (
                        <p className="mt-2 text-[12px] text-gray-500">
                            Frank has read {status.indexedPages.toLocaleString()} pages of Origins information!
                        </p>
                    )}
                </ContentBox>

                {loading && (
                    <ContentBox theme="default" tint="red" header="Recommended">
                        <p className="text-[13px] text-gray-500">Loading Frank&apos;s picks...</p>
                    </ContentBox>
                )}

                {!loading && links.length === 0 && (
                    <ContentBox theme="default" tint="red" header="Recommended">
                        <p className="text-[13px] text-gray-500">
                            Frank has not pinned any sites yet. Staff can add them in Housekeeping.
                        </p>
                    </ContentBox>
                )}

                {!loading && links.length > 0 && (
                    <ContentBox theme="default" tint="red" header="Recommended">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {links.map(link => (
                                <LinkCard key={link.id} link={link} onOpen={openLink} />
                            ))}
                        </div>
                    </ContentBox>
                )}
            </div>

            <div className="lg:col-span-1">
                <ContentBox theme="default" tint="red" header="People are asking">
                    {trending.length === 0 ? (
                        <p className="text-[13px] text-gray-500">Nothing yet. Be the first to ask Frank something.</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {trending.map(item => (
                                <button
                                    key={item.query}
                                    type="button"
                                    onClick={() => onSearch(item.query)}
                                    className="truncate border border-gray-300 bg-white px-3 py-2 text-left text-[13px] text-maroon transition-colors hover:bg-gray-100"
                                >
                                    {item.query}
                                </button>
                            ))}
                        </div>
                    )}
                </ContentBox>

                <ContentBox theme="default" tint="red" header="Back to the Hotel">
                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => window.location.assign(originsHomeUrl())}
                            className="border border-gray-300 bg-white px-3 py-2 text-left text-[13px] text-maroon hover:bg-gray-100"
                        >
                            Frankly Origins Home
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(deskPath())}
                            className="border border-gray-300 bg-white px-3 py-2 text-left text-[13px] text-maroon hover:bg-gray-100"
                        >
                            The AskFrank Desk
                        </button>
                    </div>
                </ContentBox>
            </div>
        </div>
    );
}

export default FranklyNetHome;
